import {useUpdateIssueMilestoneMutation} from '@github-ui/item-picker/useUpdateIssueMilestoneMutation'
import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import type {MilestonePickerMilestone} from '@github-ui/item-picker-milestone'
import {MilestonePicker} from '@github-ui/item-picker-milestone-tsq'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {ActionList} from '@primer/react'
import {useCallback, useMemo} from 'react'

import {LABELS} from '../../constants/labels'
import type {IssueSecondaryNode} from '../issue-viewer-secondary-view-query-types'
import type {IssueNode, IssueNodeRepository} from '../issue-viewer-view-query-types'
import {ReadonlySectionHeader} from '../ReadonlySectionHeader'
import {Section} from '../Section'
import {SectionHeader} from '../SectionHeader'
import {Milestone} from './Milestone'

export const ReadonlyMilestonesSectionHeader = () => <ReadonlySectionHeader title={LABELS.sectionTitles.milestones} />

type MilestonesSectionProps = {
  sectionHeader: React.ReactElement
  milestone: MilestonePickerMilestone | null
  className?: string
}
export const MilestonesSection = ({sectionHeader, milestone, className}: MilestonesSectionProps) => (
  <Section
    id={`sidebar-milestones-section`}
    className={className}
    sectionHeader={sectionHeader}
    emptyText={milestone ? undefined : LABELS.emptySections.milestones}
  >
    <ActionList variant="full">
      <Milestone milestone={milestone} />
    </ActionList>
  </Section>
)

// RELAY-CLEANUP(IssueViewerSecondaryViewQuery): widens nullable fields so the still-Relay parent
// data is assignable. Remove when IssueViewerSecondaryViewQuery leaves Relay.
export type MilestonesSectionMilestoneFragment = RelayCompat<Pick<IssueSecondaryNode, 'milestone'>>

export type EditIssueMilestonesSectionFragment = Pick<
  IssueNode,
  'id' | 'milestone' | 'number' | 'viewerCanSetMilestone'
> & {
  repository: Pick<IssueNodeRepository, 'name'> & {
    owner: Pick<IssueNodeRepository['owner'], 'login'>
  }
}

export type EditIssueMilestonesSectionProps = {
  issue: EditIssueMilestonesSectionFragment
  canCreateMilestone?: boolean
  onIssueUpdate?: () => void
  insideSidePanel?: boolean
  readonly: boolean
}

export function EditIssueMilestonesSection({
  issue,
  canCreateMilestone = false,
  onIssueUpdate,
  insideSidePanel,
  readonly,
}: EditIssueMilestonesSectionProps) {
  const {
    repository: {
      owner: {login: owner},
      name: repo,
    },
    id: issueId,
    number: issueNumber,
    viewerCanSetMilestone,
    milestone,
  } = issue
  const {addToast} = useToastContext()
  const updateIssueMilestone = useUpdateIssueMilestoneMutation({owner, repo, issueNumber})

  const onSelectionChanged = useCallback(
    (selectedMilestones: MilestonePickerMilestone[]) => {
      const selectedMilestone = selectedMilestones.at(-1) ?? null
      updateIssueMilestone(issueId, selectedMilestone, {
        onError: () => {
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: ERRORS.couldNotUpdateMilestone,
          })
        },
        onCompleted: () => {
          onIssueUpdate?.()
        },
      })
    },
    [addToast, issueId, onIssueUpdate, updateIssueMilestone],
  )

  const sectionHeader = useMemo(() => {
    const readonlyMilestone = readonly || !viewerCanSetMilestone
    if (readonlyMilestone) {
      return <ReadonlyMilestonesSectionHeader />
    }

    return (
      <MilestonePicker
        repo={repo}
        owner={owner}
        onSelectionChange={onSelectionChanged}
        anchorElement={(anchorProps, ref) => (
          <SectionHeader title={LABELS.sectionTitles.milestones} buttonProps={anchorProps} ref={ref} />
        )}
        readonly={readonlyMilestone}
        canCreateMilestone={canCreateMilestone}
        activeMilestone={milestone ?? null}
        insidePortal={insideSidePanel}
        showMilestoneDescription
        showNoMatchItem
      />
    )
  }, [readonly, viewerCanSetMilestone, canCreateMilestone, repo, owner, onSelectionChanged, milestone, insideSidePanel])

  return <MilestonesSection sectionHeader={sectionHeader} milestone={milestone ?? null} />
}
